import Stepper from '@/components/Stepper';
import ToolBar from '@/components/ToolBar';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { displayError } from '@/lib/error';
import { HIClientDetails } from './HIClientDetails';
import { HIServiceDetails } from './HIServiceDetails';
import { HIStep1Schema, HIStep2Schema, type HIFormValues, type HIWorkOrderCreatePayload } from './hi.schema';
import { assignHIServer, assignHomeInspectionWorkOrder, useCreateHIWorkOrder } from '@/services/homeInspectionService';
import { toast } from '@/components/ui/use-toast';
import { useUserStore } from '@/stores/user';
import { Navigate } from 'react-router-dom';
import { canAccessHomeInspectionWorkOrders } from './hiPermissions';

const STEPS = [
  { title: 'Client Information', isCompleted: false },
  { title: 'Service Details', isCompleted: false },
];

const STEP_SCHEMAS = [HIStep1Schema, HIStep2Schema];

export const HICreate = () => {
  const navigate = useNavigate();
  const loggedInUser = useUserStore((state) => state.user);
  const [activeStep, setActiveStep] = useState(0);
  const [steps, setSteps] = useState(STEPS);
  const [currentSchema, setCurrentSchema] = useState<(typeof STEP_SCHEMAS)[number]>(STEP_SCHEMAS[0]);

  const form = useForm<HIFormValues>({
    resolver: zodResolver(currentSchema),
    mode: 'all',
    defaultValues: {
      serviceType: 'HOUSE_INSPECTION',
      rushType: 'NONE',
      sameDayRushDisplay: false,
      nextDayRushDisplay: false,
      prePaid: false,
    },
  });

  useEffect(() => {
    setCurrentSchema(STEP_SCHEMAS[activeStep]);
    setTimeout(async () => {
      await form.trigger();
      form.clearErrors();
    }, 50);
  }, [activeStep]);

  const scrollToTop = () => {
    const el = document.getElementById('toolbar');
    if (el) el.scrollIntoView({ block: 'end', behavior: 'smooth' });
  };

  const nextStep = async () => {
    const isValid = await form.trigger();
    if (isValid) {
      setSteps((prev) => prev.map((s, i) => (i === activeStep ? { ...s, isCompleted: true } : s)));
      setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
      setTimeout(scrollToTop, 100);
    }
  };

  const prevStep = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
    setTimeout(scrollToTop, 100);
  };

  const { mutate: createWorkOrder, status: createStatus } = useCreateHIWorkOrder();
  const [isAssigning, setIsAssigning] = useState(false);

  if (!canAccessHomeInspectionWorkOrders(loggedInUser)) {
    return <Navigate to="/not-found" />;
  }

  const saveHandler = async () => {
    const isValid = await form.trigger();
    if (!isValid) return;

    const values = form.getValues();
    const propertyAddress = [values.addressLine1, values.addressLine2].filter(Boolean).join(' ');

    const payload: HIWorkOrderCreatePayload = {
      serviceType: 'HOUSE_INSPECTION',
      clientId: values.clientId,
      contactId: values.contactId,
      fileNumber: values.fileNumber?.trim() ?? null,
      caseNumber: values.caseNumber?.trim() ?? null,
      lawyer: values.lawyer ?? null,
      represents: values.represents ?? null,
      caseName: values.caseName?.trim() ?? null,
      rushType: values.rushType,
      mortgageCompanyOrPlaintiff: values.mortgageCompanyOrPlaintiff.trim(),
      propertyAddress,
      city: values.city.trim(),
      state: values.state,
      zipCode: values.zipCode.trim(),
      ownerOrDefendantName: values.ownerOrDefendantName.trim(),
    };

    createWorkOrder(payload, {
      onSuccess: async (workOrder) => {
        const serverId = values.serverId;
        const teamId = values.teamId;
        const userId = values.userId;

        setIsAssigning(true);
        try {
          // Assign team then user as separate XOR calls (API forbids both in one body)
          if (teamId) {
            await assignHomeInspectionWorkOrder(workOrder.workOrderId, { teamId });
          }
          if (userId) {
            await assignHomeInspectionWorkOrder(workOrder.workOrderId, { userId });
          }
          if (serverId) {
            await assignHIServer(workOrder.workOrderId, serverId);
          }
          setIsAssigning(false);
        } catch (_assignError) {
          setIsAssigning(false);
          // Work order was already created — show a clear message and navigate away
          // so the user cannot accidentally submit again and create a duplicate.
          toast({
            variant: 'destructive',
            description: `Work order ${workOrder.workOrderId} created, but assignment failed. You can update assignment from the work orders list.`,
            duration: 7000,
          });
          navigate('/home-inspection');
          return;
        }

        toast({
          variant: 'default',
          description: 'Home Inspection work order created successfully.',
          duration: 5000,
        });
        navigate('/home-inspection');
      },
      onError: (error) => {
        displayError(error, 5000);
      },
    });
  };

  return (
    <div className="w-full scrollbar-hide">
      <div className="w-full">
        <ToolBar title="Home Inspection Work Order Entry" />
        <div className="mt-3 flex justify-center pl-24">
          <Stepper steps={steps} activeStep={activeStep} goToStep={Function.prototype as (step: number) => void} />
        </div>

        <FormProvider {...form}>
          {activeStep === 0 && <HIClientDetails />}
          {activeStep === 1 && <HIServiceDetails />}
        </FormProvider>

        <div className="flex justify-end mt-4 mb-8 w-full gap-2">
          {activeStep > 0 && (
            <Button
              variant="outline"
              className="text-primary py-2 px-4 rounded-md border-primary flex justify-center w-[125px] h-[48px] hover:bg-[#EDF5FF]"
              onClick={prevStep}
            >
              Back
            </Button>
          )}
          {activeStep < steps.length - 1 && (
            <Button
              className="text-[#fff] py-2 px-4 rounded-md bg-primary w-[125px] h-[48px] border-primary flex justify-center hover:bg-[#12375C] hover:text-white disabled:bg-primary"
              onClick={nextStep}
              disabled={!form.formState.isValid}
            >
              Next
            </Button>
          )}
          {activeStep === steps.length - 1 && (
            <Button
              className="text-[#fff] py-2 px-4 rounded-md bg-primary w-[125px] h-[48px] border-primary flex justify-center hover:bg-[#12375C] hover:text-white"
              onClick={saveHandler}
              disabled={!form.formState.isValid || createStatus === 'pending' || isAssigning}
            >
              Save
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
